"use client";

import React, { useMemo, useState, useEffect } from "react";
import { Users, Edit3Icon, Plus, Trash2, X, Search, LayoutList, CheckCircle2, XCircle } from "lucide-react";
import Pagination, { usePagination } from '../shared/Pagination';
import { createPortal } from "react-dom";
import { useSuppliers } from "../context/SuppliersContext";
import { useStates } from "../context/StatesContext";
import { useDistricts } from "../context/DistrictsContext";
import { useMandals } from "../context/MandalsContext";
import { useCities } from "../context/CitiesContext";
import { useSupplierTypes } from "../context/SupplierTypesContext";
import { useLocation } from "react-router-dom";
/* ─── Reusable primitives ────────────────────────────────────── */

const Section = ({ title, children }) => (
  <div className="border border-gray-200 rounded-xl p-3 sm:p-4 space-y-3 bg-white">
    <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider border-b border-gray-100 pb-2">{title}</p>
    {children}
  </div>
);

const Input = ({ label, value, onChange, type = "text", error }) => (
  <div className="space-y-1.5">
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full px-3 sm:px-4 py-2.5 rounded-xl border outline-none bg-white text-sm transition-colors ${
        error ? "border-red-400 bg-red-50 focus:ring-2 focus:ring-red-200" : "border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
      }`}
    />
    {error && <p className="text-xs text-red-500">{error}</p>}
  </div>
);

const FormSelect = ({ label, value, onChange, options, disabled = false }) => (
  <div className="space-y-1.5">
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full px-3 sm:px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    >
      <option value="">Select…</option>
      {options.map((o) => (
        <option key={String(o.value)} value={String(o.value)}>
          {o.label}
        </option>
      ))}
    </select>
  </div>
);

/* Plain, unlabeled select used in the top filter bar (matches Sub-Categories page) */
const FilterSelect = ({ value, onChange, children }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full sm:w-auto px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:bg-white transition"
  >
    {children}
  </select>
);

const Toggle = ({ checked, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    style={{ backgroundColor: checked ? "#16a34a" : "#d1d5db" }}
    className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200"
  >
    <span
      style={{ transform: checked ? "translateX(1.375rem)" : "translateX(0.25rem)" }}
      className="inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200"
    />
  </button>
);

const StatusBtn = ({ value, current, set }) => {
  const isSelected = current === value;
  return (
    <button
      type="button"
      onClick={() => set(value)}
      className={`flex-1 py-2.5 rounded-lg text-sm font-medium capitalize transition-all ${
        isSelected
          ? value === "active" ? "bg-white text-green-700 shadow-sm ring-1 ring-green-200" : "bg-white text-red-600 shadow-sm ring-1 ring-red-200"
          : "text-gray-500 hover:text-gray-700"
      }`}
    >
      {value}
    </button>
  );
};

const Stat = ({ label, value, icon: Icon, accent, color }) => (
  <div className={`bg-white rounded-xl border border-gray-100 border-l-4 ${accent} p-2.5 sm:p-4 flex items-center gap-2 sm:gap-3 shadow-sm min-w-0`}>
    <div className="w-7 h-7 sm:w-9 sm:h-9 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0">
      <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${color}`} />
    </div>
    <div className="min-w-0">
      <p className={`text-base sm:text-xl font-bold ${color} leading-tight`}>{value}</p>
      <p className="text-[10px] sm:text-xs text-gray-500 leading-tight truncate">{label}</p>
    </div>
  </div>
);

const Actions = ({ item, onEdit, onDelete }) => (
  <div className="flex items-center gap-2 sm:gap-3">
    <button
      type="button"
      onClick={onEdit}
      className="text-teal-600 hover:text-teal-800 p-1"
    >
      <Edit3Icon size={16} />
    </button>
    <button
      type="button"
      onClick={() => onDelete(item.id)}
      className="text-red-500 hover:text-red-700 p-1"
    >
      <Trash2 size={16} />
    </button>
  </div>
);

/* ─── Spinner ────────────────────────────────────────────────── */

const Spinner = () => (
  <svg
    className="animate-spin h-4 w-4 text-white"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

/* ─── Helpers to resolve supplier type fields safely ─────────── */

const resolveSupplierTypeName = (item) =>
  item.customer_type_name ||
  item.supplier_type_name ||
  item.customerTypeName ||
  item.supplierTypeName ||
  item.customer_type ||
  item.supplier_type ||
  null;

const resolveSupplierTypeId = (item) => {
  const raw =
    item.customer_type_id ??
    item.customerTypeId ??
    item.supplier_type_id ??
    item.supplierTypeId ??
    "";
  return String(raw);
};

/* ─── Blank form ─────────────────────────────────────────────── */

const BLANK = {
  firstName: "",
  lastName: "",
  phone: "",
  address: "",
  creditLimit: "",
  email: "",
  gender: "",
  pincode: "",
  customerType: "",
  outstanding: "",
  status: "active",
  stateId: "",
  districtId: "",
  mandalId: "",
  cityId: "",
};

/* ─── Page ───────────────────────────────────────────────────── */

export default function SuppliersPage() {
  const {
    suppliers,
    loading,
    addSupplier,
    updateSupplier,
    deleteSupplier,
    toggleSupplierStatus,
  } = useSuppliers();

  const { states, fetchStates } = useStates();
  const { districts, fetchDistricts } = useDistricts();
  const { mandals, fetchMandals } = useMandals();
  const { cities, fetchCities } = useCities();
  const { supplierTypes, fetchSupplierTypes } = useSupplierTypes();

const location = useLocation();
const [search, setSearch] = useState(location.state?.prefillSearch || "");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("az");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [formLoading, setFormLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Fetch initial data
  useEffect(() => {
    fetchStates();
    fetchSupplierTypes();
  }, []);

  const set = (key) => (val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  /* ── Status toggle ──────────────────────────────────────────── */
  const handleToggleStatus = async (item) => {
    const newStatus = item.status === "active" ? "inactive" : "active";
    try {
      if (typeof toggleSupplierStatus === "function") {
        await toggleSupplierStatus(item.id, item.status);
      } else {
        await updateSupplier(item.id, { ...item, status: newStatus });
      }
    } catch (err) {
      console.error("Toggle status error:", err);
      alert(err.message || "Could not update status");
    }
  };

  /* ── Location cascade handlers ────────────────────────────── */
  const handleStateChange = async (val) => {
    setForm((f) => ({ ...f, stateId: val, districtId: "", mandalId: "", cityId: "" }));
    if (val) await fetchDistricts(val);
  };

  const handleDistrictChange = async (val) => {
    setForm((f) => ({ ...f, districtId: val, mandalId: "", cityId: "" }));
    if (val) await fetchMandals(val);
  };

  const handleMandalChange = async (val) => {
    setForm((f) => ({ ...f, mandalId: val, cityId: "" }));
    if (val) await fetchCities(val);
  };

  /* ── Open Add ──────────────────────────────────────────────── */
  const openAdd = () => {
    setEditing(null);
    setForm(BLANK);
    setErrors({});
    setModalOpen(true);
  };

  /* ── Open Edit ─────────────────────────────────────────────── */
  const openEdit = async (item) => {
    setFormLoading(true);
    setErrors({});
    setModalOpen(true);
    setEditing(item);

    try {
      await Promise.all([fetchSupplierTypes(), fetchStates()]);

      const stateId    = String(item.state_id    || item.stateId    || item.state    || "");
      const districtId = String(item.district_id || item.districtId || item.district || "");
      const mandalId   = String(item.mandal_id   || item.mandalId   || item.mandal   || "");
      const cityId     = String(item.city_id     || item.cityId     || item.city     || "");

      const locationFetches = [];
      if (stateId)    locationFetches.push(fetchDistricts(stateId));
      if (districtId) locationFetches.push(fetchMandals(districtId));
      if (mandalId)   locationFetches.push(fetchCities(mandalId));
      await Promise.all(locationFetches);

      setForm({
        ...BLANK,
        firstName:    item.first_name   || "",
        lastName:     item.last_name    || "",
        phone:        item.phone        || "",
        address:      item.address      || "",
        creditLimit:  item.credit_limit || "",
        email:        item.email        || "",
        gender:       item.gender       || "",
        pincode:      item.pincode      || "",
        customerType: resolveSupplierTypeId(item),
        outstanding:  item.outstanding  || "",
        status:       item.status       || "active",
        stateId,
        districtId,
        mandalId,
        cityId,
      });
    } catch (err) {
      console.error("Error loading edit form:", err);
      alert("Error loading supplier data");
    } finally {
      setFormLoading(false);
    }
  };

  /* ── Save ──────────────────────────────────────────────────── */
  const save = async () => {
    const newErrors = {};

    if (!form.firstName.trim()) {
      newErrors.firstName = "First name is required.";
    }

    const phoneRegex = /^[6-9]\d{9}$/;
    if (!form.phone.trim()) {
      newErrors.phone = "Phone number is required.";
    } else if (!phoneRegex.test(form.phone.trim())) {
      newErrors.phone = "Enter a valid 10-digit Indian mobile number (starts with 6–9).";
    }

    if (form.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email.trim())) {
        newErrors.email = "Enter a valid email address.";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    const payload = {
      first_name:       form.firstName,
      last_name:        form.lastName,
      phone:            form.phone,
      address:          form.address,
      credit_limit:     form.creditLimit,
      email:            form.email,
      gender:           form.gender,
      pincode:          form.pincode,
      customer_type_id: form.customerType || null,
      customer_type:    form.customerType || null,
      outstanding:      form.outstanding,
      status:           form.status,
      state:            form.stateId,
      district:         form.districtId,
      mandal:           form.mandalId,
      city:             form.cityId,
    };

    setSaving(true);
    try {
      if (editing) {
        await updateSupplier(editing.id, payload);
      } else {
        await addSupplier(payload);
      }
      setModalOpen(false);
      setEditing(null);
      setForm(BLANK);
    } catch (err) {
      console.error("Save error:", err);
      alert(err.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ────────────────────────────────────────────────── */
  const handleDelete = (id) => {
    if (window.confirm("Delete this supplier?")) deleteSupplier(id);
  };

  /* ── Filter + sort ─────────────────────────────────────────── */
  const filtered = useMemo(() => {
    let list = suppliers.filter((s) =>
      `${s.first_name} ${s.last_name}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );

    if (typeFilter !== "all") {
      list = list.filter((s) => resolveSupplierTypeId(s) === typeFilter);
    }

    if (statusFilter !== "all") {
      list = list.filter((s) => s.status === statusFilter);
    }

    list = [...list].sort((a, b) => {
      const nameA = `${a.first_name} ${a.last_name}`.trim().toLowerCase();
      const nameB = `${b.first_name} ${b.last_name}`.trim().toLowerCase();
      return sortOrder === "az" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });

    return list;
  }, [suppliers, search, typeFilter, statusFilter, sortOrder]);

  const {
    page: supPage,
    pageSize: supSize,
    setPage: setSupPage,
    setPageSize: setSupSize,
    totalPages: supTotalPages,
    totalItems: supTotal,
    paginated: paginatedSupp,
  } = usePagination(filtered, 10);

  /* ─── Render ─────────────────────────────────────────────── */
  return (
    <div className="p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6 w-full overflow-x-hidden">

      {/* STATS */}
      <div className="grid grid-cols-1 xs:grid-cols-3 sm:grid-cols-3 gap-2 sm:gap-4">
        <Stat label="Total Suppliers" value={suppliers.length} icon={LayoutList} accent="border-l-teal-500" color="text-teal-700" />
        <Stat label="Active"   value={suppliers.filter(s => s.status === "active").length}   icon={CheckCircle2} accent="border-l-green-500" color="text-green-600" />
        <Stat label="Inactive" value={suppliers.filter(s => s.status === "inactive").length} icon={XCircle}      accent="border-l-red-400"   color="text-red-500" />
      </div>

      {/* FILTER BAR — matches Sub-Categories layout */}
      <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm space-y-3">
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              placeholder="Search suppliers…"
              className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:bg-white transition"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <FilterSelect value={typeFilter} onChange={setTypeFilter}>
            <option value="all">All Types ({suppliers.length})</option>
            {supplierTypes.map((t) => {
              const count = suppliers.filter((s) => resolveSupplierTypeId(s) === String(t.id)).length;
              return (
                <option key={t.id} value={String(t.id)}>
                  {(t.type || t.name || t.supplier_type || "Type")} ({count})
                </option>
              );
            })}
          </FilterSelect>

          <FilterSelect value={statusFilter} onChange={setStatusFilter}>
            <option value="all">All Status ({suppliers.length})</option>
            <option value="active">Active ({suppliers.filter(s => s.status === "active").length})</option>
            <option value="inactive">Inactive ({suppliers.filter(s => s.status === "inactive").length})</option>
          </FilterSelect>

          <FilterSelect value={sortOrder} onChange={setSortOrder}>
            <option value="az">Name A → Z</option>
            <option value="za">Name Z → A</option>
          </FilterSelect>

          <button
            type="button"
            onClick={openAdd}
            className="flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2.5 sm:py-2 rounded-xl font-semibold text-sm transition-colors shadow-sm flex-shrink-0 w-full sm:w-auto"
          >
            <Plus className="w-4 h-4" /><span>Add Supplier</span>
          </button>
        </div>

        <p className="text-xs text-gray-400 px-1">
          Showing {filtered.length} of {suppliers.length} suppliers
        </p>
      </div>

      {/* TABLE — desktop/tablet (lg+) */}
      <div className="hidden lg:block overflow-x-auto bg-white rounded-xl shadow-sm border">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-teal-700 text-white">
            <tr>
              {["ID", "Name", "Supplier Type", "Phone", "Address", "Credit Limit", "Outstanding", "Status", ""].map((h) => (
                <th key={h} className="px-4 lg:px-6 py-3.5 text-xs font-semibold uppercase tracking-wider whitespace-nowrap text-left last:text-right">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="text-center py-8 text-gray-400">Loading...</td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center py-8 text-gray-400">No suppliers found</td>
              </tr>
            ) : (
              paginatedSupp.map((item, idx) => (
                <tr
                  key={item.id}
                  className={`border-t transition-colors hover:bg-teal-50/40 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}
                >
                  <td className="px-4 lg:px-6 py-4 text-xs text-gray-400">{idx + 1}</td>
                  <td className="px-4 lg:px-6 py-4 font-medium whitespace-nowrap">
                    {item.first_name} {item.last_name}
                  </td>
                  <td className="px-4 lg:px-6 py-4 text-gray-600">
                    {resolveSupplierTypeName(item) ?? "-"}
                  </td>
                  <td className="px-4 lg:px-6 py-4 text-gray-600">{item.phone}</td>
                  <td className="px-4 lg:px-6 py-4 text-gray-600 max-w-[150px] lg:max-w-[180px] truncate">
                    {item.address || "-"}
                  </td>
                  <td className="px-4 lg:px-6 py-4 text-gray-600 whitespace-nowrap">
                    ₹ {item.credit_limit || 0}
                  </td>
                  <td className="px-4 lg:px-6 py-4 text-gray-600 whitespace-nowrap">
                    ₹ {item.outstanding || 0}
                  </td>
                  <td className="px-4 lg:px-6 py-4">
                    <Toggle
                      checked={item.status === "active"}
                      onToggle={() => handleToggleStatus(item)}
                    />
                  </td>
                  <td className="px-4 lg:px-6 py-4">
                    <div className="flex justify-end">
                      <Actions item={item} onEdit={() => openEdit(item)} onDelete={handleDelete} />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {supTotalPages > 1 && (
          <div className="px-4 border-t border-gray-100">
            <Pagination
              currentPage={supPage}
              totalPages={supTotalPages}
              totalItems={supTotal}
              pageSize={supSize}
              onPageChange={setSupPage}
              onPageSizeChange={setSupSize}
            />
          </div>
        )}
      </div>

      {/* CARDS — mobile & tablet (below lg) */}
      <div className="lg:hidden space-y-3">
        {loading ? (
          <div className="text-center py-8 text-gray-400">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 text-gray-400">No suppliers found</div>
        ) : (
          paginatedSupp.map((item) => (
            <div
              key={item.id}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4 space-y-2"
            >
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm truncate">
                    {item.first_name} {item.last_name}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                    {resolveSupplierTypeName(item) ?? "No type"}
                  </p>
                </div>
                <Actions item={item} onEdit={() => openEdit(item)} onDelete={handleDelete} />
              </div>

              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-500">
                <p className="truncate">📞 {item.phone}</p>
                <p className="text-right truncate">{item.address || "-"}</p>
                <p className="truncate">Credit: ₹{item.credit_limit || 0}</p>
                <p className="text-right truncate">Due: ₹{item.outstanding || 0}</p>
              </div>

              <div className="flex justify-between items-center pt-1 border-t border-gray-100">
                <span className="text-xs text-gray-500">Status</span>
                <Toggle
                  checked={item.status === "active"}
                  onToggle={() => handleToggleStatus(item)}
                />
              </div>
            </div>
          ))
        )}

        {supTotalPages > 1 && (
          <div className="bg-white rounded-xl shadow-sm border px-3 sm:px-4">
            <Pagination
              currentPage={supPage}
              totalPages={supTotalPages}
              totalItems={supTotal}
              pageSize={supSize}
              onPageChange={setSupPage}
              onPageSizeChange={setSupSize}
            />
          </div>
        )}
      </div>

      {/* MODAL */}
      {modalOpen &&
        createPortal(
          <div className="fixed inset-0 z-50 flex justify-center sm:justify-end">
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => { if (!saving) setModalOpen(false); }}
            />
            <div
              className="relative w-full sm:max-w-md h-full max-h-full bg-white shadow-2xl flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex-shrink-0 bg-teal-700 px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between sticky top-0 z-10">
                <h2 className="text-base sm:text-lg font-semibold text-white tracking-tight">
                  {editing ? "Edit Supplier" : "Add New Supplier"}
                </h2>
                <button
                  type="button"
                  onClick={() => { if (!saving) setModalOpen(false); }}
                  disabled={saving}
                  className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X size={16} className="text-white" />
                </button>
              </div>

              {/* Form */}
              <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-4 sm:space-y-5">
                {formLoading ? (
                  <div className="text-center py-8 text-gray-500">
                    Loading supplier data...
                  </div>
                ) : (
                  <>
                    <Section title="Basic Info">
                      <Input
                        label="First Name *"
                        value={form.firstName}
                        onChange={set("firstName")}
                        error={errors.firstName}
                      />
                      <Input
                        label="Last Name"
                        value={form.lastName}
                        onChange={set("lastName")}
                      />
                      <FormSelect
                        label="Supplier Type"
                        value={form.customerType}
                        onChange={set("customerType")}
                        options={supplierTypes
                          .filter(
                            (t) =>
                              t.status === "active" ||
                              String(t.id) === String(form.customerType)
                          )
                          .map((t) => ({
                            value: String(t.id),
                            label: t.type || t.name || t.supplier_type || "",
                          }))}
                      />
                    </Section>

                    <Section title="Contact">
                      <Input
                        label="Email"
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        error={errors.email}
                      />
                      <Input
                        label="Phone *"
                        value={form.phone}
                        onChange={set("phone")}
                        error={errors.phone}
                      />
                      <FormSelect
                        label="Gender"
                        value={form.gender}
                        onChange={set("gender")}
                        options={[
                          { value: "male",   label: "Male"   },
                          { value: "female", label: "Female" },
                        ]}
                      />
                    </Section>

                    <Section title="Location">
                      <FormSelect
                        label="State"
                        value={form.stateId}
                        onChange={handleStateChange}
                        options={states.map((s) => ({ value: String(s.id), label: s.name }))}
                      />
                      <FormSelect
                        label="District"
                        value={form.districtId}
                        onChange={handleDistrictChange}
                        options={districts
                          .filter((d) => String(d.state_id || d.stateId || "") === String(form.stateId))
                          .map((d) => ({ value: String(d.id), label: d.name }))}
                        disabled={!form.stateId}
                      />
                      <FormSelect
                        label="Mandal"
                        value={form.mandalId}
                        onChange={handleMandalChange}
                        options={mandals
                          .filter((m) => String(m.district_id || m.districtId || "") === String(form.districtId))
                          .map((m) => ({ value: String(m.id), label: m.name }))}
                        disabled={!form.districtId}
                      />
                      <FormSelect
                        label="Area"
                        value={form.cityId}
                        onChange={set("cityId")}
                        options={cities
                          .filter((c) => String(c.mandal_id || c.mandalId || "") === String(form.mandalId))
                          .map((c) => ({ value: String(c.id), label: c.name }))}
                        disabled={!form.mandalId}
                      />
                      <Input
                        label="Pincode"
                        value={form.pincode}
                        onChange={set("pincode")}
                      />
                    </Section>

                    <Section title="Address">
                      <textarea
                        value={form.address}
                        onChange={(e) => set("address")(e.target.value)}
                        placeholder="Enter address..."
                        rows={3}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-gray-200 outline-none bg-white resize-none text-sm sm:text-base focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                      />
                    </Section>

                    <Section title="Finance">
                      <Input
                        label="Credit Limit"
                        value={form.creditLimit}
                        onChange={set("creditLimit")}
                      />
                      <Input
                        label="Outstanding"
                        value={form.outstanding}
                        onChange={set("outstanding")}
                      />
                    </Section>

                    <Section title="Status">
                      <div className="flex gap-2 p-1 bg-gray-100 rounded-xl">
                        <StatusBtn value="active"   current={form.status} set={set("status")} />
                        <StatusBtn value="inactive" current={form.status} set={set("status")} />
                      </div>
                    </Section>

                    {/* Footer buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-100">
                      <button
                        type="button"
                        onClick={() => setModalOpen(false)}
                        disabled={saving}
                        className="flex-1 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors order-2 sm:order-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={save}
                        disabled={saving}
                        className="flex-1 py-2.5 rounded-xl bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 transition-colors shadow-sm order-1 sm:order-2 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {saving ? (
                          <>
                            <Spinner />
                            {editing ? "Updating…" : "Adding…"}
                          </>
                        ) : (
                          editing ? "Update Supplier" : "Add Supplier"
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}